import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Mail, X, Plus, Download } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface EmailChecklistButtonProps {
  checklistId: string;
  defaultRecipients?: string[];
}

const EmailChecklistButton: React.FC<EmailChecklistButtonProps> = ({ checklistId, defaultRecipients = [''] }) => {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [recipients, setRecipients] = useState<string[]>(defaultRecipients);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [attachPdf, setAttachPdf] = useState(true);
  const [sending, setSending] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [progress, setProgress] = useState('');

  const addRecipient = () => {
    setRecipients([...recipients, '']);
  };

  const updateRecipient = (index: number, value: string) => {
    const updated = [...recipients];
    updated[index] = value;
    setRecipients(updated);
  };

  const removeRecipient = (index: number) => {
    if (recipients.length > 1) {
      const updated = recipients.filter((_, i) => i !== index);
      setRecipients(updated);
    }
  };

  const handleGeneratePdfOnly = async () => {
    setGeneratingPdf(true);
    setProgress('Generating PDF...');
    
    try {
      const { data, error } = await supabase.functions.invoke('generate-checklist-pdf', {
        body: { checklistId },
      });
      
      if (error) throw error;
      if (!data?.pdfBase64) throw new Error('PDF generation failed');
      
      // Create download link for PDF
      const byteCharacters = atob(data.pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "Success",
        description: "PDF generated and downloaded successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to generate PDF. Please try again.",
        variant: "destructive",
      });
    } finally {
      setGeneratingPdf(false);
      setProgress('');
    }
  };

  const handleSend = async () => {
    const validRecipients = recipients.filter(email => 
      email.trim() && email.includes('@')
    );

    if (validRecipients.length === 0) {
      toast({
        title: "Error",
        description: "Please enter at least one valid email address.",
        variant: "destructive",
      });
      return;
    }

    setSending(true);

    try {
      let generatedPdf: { pdfBase64: string; filename: string } | null = null;

      if (attachPdf) {
        setProgress('Generating PDF...');
        const { data: genData, error: genError } = await supabase.functions.invoke('generate-checklist-pdf', {
          body: { checklistId },
        });
        if (genError) throw genError;
        if (!genData?.pdfBase64) {
          throw new Error('PDF generation failed. Please try again.');
        }
        generatedPdf = { pdfBase64: genData.pdfBase64, filename: genData.filename };
      }

      setProgress('Sending email...');
      const { data, error } = await supabase.functions.invoke('email-checklist-report', {
        body: {
          checklistId,
          recipients: validRecipients,
          subject: subject.trim() || undefined,
          message: message.trim() || undefined,
          attachPdf,
          pdfBase64: generatedPdf?.pdfBase64,
          pdfFilename: generatedPdf?.filename,
        },
      });

      if (error) throw error;

      // Show specific feedback based on PDF generation success
      let successMessage = `Checklist report sent to ${validRecipients.length} recipient${validRecipients.length === 1 ? '' : 's'}`;
      
      if (attachPdf && data?.pdfGenerated === false) {
        successMessage += " (PDF generation failed - sent without attachment)";
        toast({
          title: "Partial Success",
          description: successMessage,
          variant: "default",
        });
      } else if (attachPdf && data?.pdfGenerated === true) {
        successMessage += " with PDF attachment";
        toast({
          title: "Success",
          description: successMessage,
        });
      } else {
        toast({
          title: "Success",
          description: successMessage,
        });
      }

      setIsOpen(false);
      setRecipients(defaultRecipients);
      setSubject('');
      setMessage('');
      setAttachPdf(true);
    } catch (error: any) {
      console.error('Error sending email:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
      setProgress('');
    }
  };

  return (
    <>
      {/* Generate PDF Only Button */}
      <Button 
        size="sm" 
        variant="outline" 
        onClick={handleGeneratePdfOnly}
        disabled={generatingPdf}
        className="glass-button mr-2"
      >
        <Download className="w-4 h-4 mr-1" />
        {generatingPdf ? 'Generating...' : 'PDF'}
      </Button>

      {/* Email Dialog Button */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button size="sm" variant="outline" className="cta-secondary">
            <Mail className="w-4 h-4 mr-1" />
            Email
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md glass-panel max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-crystal-white">Email Checklist Report</DialogTitle>
          </DialogHeader>
          
          {progress && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 mb-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-blue-300 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm text-blue-300">{progress}</span>
              </div>
            </div>
          )}
          
          <ScrollArea className="flex-1 -mx-6 px-6">
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="recipients" className="text-crystal-white">Recipients</Label>
                <div className="space-y-2 mt-1">
                  {recipients.map((email, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        type="email"
                        placeholder="Enter email address"
                        value={email}
                        onChange={(e) => updateRecipient(index, e.target.value)}
                        className="glass-input flex-1"
                      />
                      {recipients.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeRecipient(index)}
                          className="glass-button"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addRecipient}
                    className="glass-button"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Recipient
                  </Button>
                </div>
              </div>

              <div>
                <Label htmlFor="subject" className="text-crystal-white">Subject (optional)</Label>
                <Input
                  id="subject"
                  placeholder="Custom email subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="glass-input mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Leave blank to use default subject line
                </p>
              </div>

              <div>
                <Label htmlFor="message" className="text-crystal-white">Additional Message (optional)</Label>
                <Textarea
                  id="message"
                  placeholder="Add a custom message to include with the report"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="glass-input mt-1"
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="attachPdf" className="text-crystal-white">Attachment Options</Label>
                <div className="flex items-center space-x-2 mt-2">
                  <input
                    type="checkbox"
                    id="attachPdf"
                    checked={attachPdf}
                    onChange={(e) => setAttachPdf(e.target.checked)}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <label htmlFor="attachPdf" className="text-sm text-crystal-white">
                    Attach PDF version
                  </label>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  PDF attachment provides a professional, printable version of the report
                </p>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-blue-500/20 text-blue-300">Info</Badge>
                  <span className="text-sm text-blue-300 font-medium">What will be sent:</span>
                </div>
                <ul className="text-xs text-blue-200 space-y-1 ml-2">
                  <li>• Complete checklist report with all recorded data</li>
                  <li>• Item statuses, measurements, and notes</li>
                  <li>• Evidence indicators (photos, videos, voice notes)</li>
                  <li>• Completion details and signatures</li>
                  {attachPdf && <li>• Professional PDF attachment for printing/archiving</li>}
                </ul>
              </div>
            </div>
          </ScrollArea>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsOpen(false)} className="glass-button">
              Cancel
            </Button>
            <Button onClick={handleSend} disabled={sending} className="cta-primary">
              {sending ? (progress || 'Sending...') : 'Send Report'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EmailChecklistButton;